import { buildInstallmentSchedule, InstallmentScheduleError } from "@amzn/awsmp-epo-utils/payment-schedule";
import { type GenerateInstallmentSchedule } from "@amzn/awsmp-max-model";

const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

// Runs the shared epo-utils generator over the buyer's FPS inputs and returns the
// rows in AIPO wire shape (`chargeDate` ISO-date-only + the generator's 2dp
// decimal `chargeAmount`), plus a positional `paymentNumber`. The buyer-flow
// tables format these at the cell; the FPS commit threads the wire fields
// straight to AIPO. One computation feeds both, so the schedule a buyer reviews
// is exactly the schedule committed. Returns null when inputs are incomplete or
// the generator rejects them — callers hide their tables and refuse to commit a
// non-derivable schedule (F4).
export const generateInstallmentSchedule: GenerateInstallmentSchedule = (inputs) => {
  if (inputs.frequency === undefined || inputs.firstInvoiceDate === "" || !inputs.durationISO) {
    return null;
  }

  const firstPaymentDate = parseUtcDateOnly(inputs.firstInvoiceDate);
  if (firstPaymentDate === null) {
    return null;
  }

  const dayOfMonth = inputs.dayOfMonth === "" ? undefined : Number(inputs.dayOfMonth);
  const initialPayment = inputs.initialPayment === "" ? undefined : Number(inputs.initialPayment);

  try {
    const schedule = buildInstallmentSchedule({
      contractWindowAnchor: inputs.anchorDate,
      firstPaymentDate,
      durationISO: inputs.durationISO,
      frequency: inputs.frequency,
      total: inputs.total,
      dayOfMonth,
      initialPayment,
    });
    return schedule.map((row, index) => ({
      paymentNumber: index + 1,
      chargeDate: formatChargeDateIso(row.date),
      chargeAmount: row.amount,
    }));
  } catch (error) {
    if (error instanceof InstallmentScheduleError) {
      return null;
    }
    throw error;
  }
};

// Parses a "YYYY-MM-DD" string to a UTC Date, returning null for malformed or
// impossible calendar dates (e.g. "2026-02-30"), which Date would otherwise roll
// forward silently. The upstream field validators already gate this input, so this
// is a defensive boundary rather than the primary error path.
function parseUtcDateOnly(value: string): Date | null {
  const match = ISO_DATE_ONLY.exec(value);
  if (match === null) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }

  return date;
}

// CAPI ChargeDate wire format: ISO-8601 UTC date-only (YYYY-MM-DD). The
// generator's dates are UTC midnight, so the date half of the ISO string is the
// charge date.
function formatChargeDateIso(date: Date): string {
  return date.toISOString().slice(0, 10);
}
